import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export function PageHeader({ eyebrow, title, subtitle, actions }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end gap-4 mb-5 sm:mb-8">
      <div className="min-w-0">
        {eyebrow && <div className="eyebrow mb-1.5">{eyebrow}</div>}
        <h1 className="text-2xl sm:text-4xl font-bold tracking-tight break-words" data-testid="page-title">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-1.5">{subtitle}</p>}
      </div>
      {actions && <div className="sm:ml-auto flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function Panel({ title, action, children, className = "", testid }) {
  return (
    <section className={`rounded-xl bg-card border shadow-sm min-w-0 ${className}`} data-testid={testid}>
      {title && (
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 px-4 sm:px-5 py-3.5 sm:py-4 border-b">
          <h3 className="font-heading font-semibold text-base">{title}</h3>
          {action && <div className="ml-auto">{action}</div>}
        </div>
      )}
      <div className="p-4 sm:p-5">{children}</div>
    </section>
  );
}

const TONES = {
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-400 dark:border-emerald-900",
  amber: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/50 dark:text-amber-400 dark:border-amber-900",
  rose: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/50 dark:text-rose-400 dark:border-rose-900",
  blue: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/50 dark:text-blue-400 dark:border-blue-900",
  slate: "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",
  indigo: "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/50 dark:text-indigo-400 dark:border-indigo-900",
};

const STATUS_TONE = {
  Scheduled: "blue", Ongoing: "amber", Completed: "emerald", Billed: "indigo", Cancelled: "rose", "Pending Approval": "amber", Rejected: "rose",
  Paid: "emerald", Partial: "amber", Unpaid: "rose", Active: "emerald", Inactive: "slate",
  Draft: "slate", Sent: "blue", Approved: "emerald", "Pending Approval": "amber", "In Meta · Paused": "amber", Failed: "rose", Accepted: "emerald", Rejected: "rose", own: "indigo", market: "amber",
};

export function StatusBadge({ value, testid }) {
  const label = value === "own" ? "Own" : value === "market" ? "Market" : value;
  return (
    <Badge variant="outline" data-testid={testid} className={`font-semibold whitespace-nowrap ${TONES[STATUS_TONE[value] || "slate"]}`}>
      {label || "-"}
    </Badge>
  );
}

export function EmptyState({ icon: Icon, title, text, action }) {
  return (
    <div className="py-14 text-center" data-testid="empty-state">
      {Icon && <Icon className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />}
      <div className="font-heading font-semibold">{title}</div>
      {text && <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">{text}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ConfirmDelete({ onConfirm, trigger, title = "Delete this record?", text = "This action cannot be undone." }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{text}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-cancel-btn">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} data-testid="confirm-delete-btn" className="bg-rose-600 hover:bg-rose-700">
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Mobile record cards: shown below md, tables shown from md up
export function MobileCards({ items, render, testid }) {
  return <div className="md:hidden space-y-3" data-testid={testid}>{items.map(render)}</div>;
}

export function MobileCard({ title, subtitle, right, rows = [], actions, onClick, testid }) {
  return (
    <div className="rounded-lg border bg-card p-4 active:bg-muted/50 transition-colors" onClick={onClick} data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-semibold break-words">{title}</div>
          {subtitle && <div className="text-xs text-muted-foreground mt-0.5 break-words">{subtitle}</div>}
        </div>
        {right && <div className="shrink-0 text-right">{right}</div>}
      </div>
      {rows.length > 0 && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3 text-sm">
          {rows.filter(Boolean).map(([l, v]) => (
            <div key={l} className="min-w-0"><div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{l}</div><div className="break-words">{v}</div></div>
          ))}
        </div>
      )}
      {actions && <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t" onClick={(e) => e.stopPropagation()}>{actions}</div>}
    </div>
  );
}
